/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setModal } from 'modules/_shared/redux/modalActions';
import normalizeCif from 'utils/normalizeCif';
import {
  getEmployeesComparison,
  synchronizeEmployees,
} from 'modules/beneficiaries/redux/employeeActions';

const DEFAULT_ROWS_PER_PAGE = 10;

export const useSyncEmployees = () => {
  const dispatch = useDispatch();
  const actualSociety = useSelector((state) => state.society?.actualSociety);

  const [comparison, setComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notMatchingSelections, setNotMatchingSelections] = useState({});
  const [isSynchronizing, setIsSynchronizing] = useState(false);
  const [matchingPagination, setMatchingPagination] = useState({
    page: 1,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  });
  const [notMatchingPagination, setNotMatchingPagination] = useState({
    page: 1,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  });

  useEffect(() => {
    const fetchComparison = async () => {
      if (!actualSociety?._id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const result = await dispatch(
          getEmployeesComparison(actualSociety._id)
        );
        setComparison(result);
        setNotMatchingSelections({});
        setMatchingPagination({ page: 1, rowsPerPage: DEFAULT_ROWS_PER_PAGE });
        setNotMatchingPagination({
          page: 1,
          rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        });
      } catch (err) {
        setError(err?.message || 'Error loading comparison');
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparison();
  }, [actualSociety?._id, dispatch]);

  const notMatchingList = useMemo(
    () => comparison?.notMatching || [],
    [comparison?.notMatching]
  );

  const matchingEntries = useMemo(
    () =>
      comparison?.matching
        ? Object.entries(comparison.matching).map(([, pair]) => pair)
        : [],
    [comparison?.matching]
  );

  const employeeOptions = useMemo(
    () =>
      (comparison?.employees || []).map((emp) => ({
        value: emp._id,
        label: `${emp.name}${emp.email ? ` (${emp.email})` : ''}`,
        externalId: emp.externalId,
        beneficiary: emp.beneficiary,
      })),
    [comparison?.employees]
  );

  const alreadyLinkedEmployeeIds = useMemo(
    () =>
      (comparison?.employees || [])
        .filter((emp) => emp.beneficiary)
        .map((emp) => emp._id),
    [comparison?.employees]
  );

  const matchingPaginated = useMemo(() => {
    const { page, rowsPerPage } = matchingPagination;
    const start = (page - 1) * rowsPerPage;
    return matchingEntries.slice(start, start + rowsPerPage);
  }, [matchingEntries, matchingPagination]);

  const notMatchingPaginated = useMemo(() => {
    const { page, rowsPerPage } = notMatchingPagination;
    const start = (page - 1) * rowsPerPage;
    return notMatchingList.slice(start, start + rowsPerPage);
  }, [notMatchingList, notMatchingPagination]);

  const firstMatchingNr =
    (matchingPagination.page - 1) * matchingPagination.rowsPerPage + 1;
  const firstNotMatchingNr =
    (notMatchingPagination.page - 1) * notMatchingPagination.rowsPerPage + 1;

  const hasUpdatesToSync =
    matchingEntries.length > 0 ||
    notMatchingList.some((b) => notMatchingSelections[b._id]);

  const handleClose = () => dispatch(setModal(null));

  const handleSynchronize = async () => {
    if (!actualSociety?._id || !comparison) return;

    const matching = matchingEntries.map(({ beneficiary, employee }) => ({
      beneficiaryId: beneficiary._id,
      employeeId: employee._id,
      employeeExternalId: employee.externalId,
    }));

    const notMatching = notMatchingList
      .filter((b) => notMatchingSelections[b._id])
      .map((b) => {
        const selection = notMatchingSelections[b._id];
        return {
          beneficiaryId: b._id,
          employeeId: selection.value,
          employeeExternalId: selection.externalId,
        };
      });

    if (matching.length === 0 && notMatching.length === 0) return;

    try {
      setIsSynchronizing(true);
      await dispatch(
        synchronizeEmployees(actualSociety._id, { matching, notMatching })
      );
      handleClose();
    } catch {
      // Error already handled by action
    } finally {
      setIsSynchronizing(false);
    }
  };

  const handleNotMatchingSelect = (beneficiaryId, selectedOption) => {
    setNotMatchingSelections((prev) => {
      const beneficiary = notMatchingList.find((b) => b._id === beneficiaryId);
      const normalizedCif =
        (beneficiary?.cif && normalizeCif(beneficiary.cif)) || '';

      const beneficiaryIdsWithSameCif = normalizedCif
        ? notMatchingList
            .filter((b) => normalizeCif(b.cif || '') === normalizedCif)
            .map((b) => b._id)
        : [beneficiaryId];

      const next = { ...prev };
      beneficiaryIdsWithSameCif.forEach((id) => {
        next[id] = selectedOption;
      });
      return next;
    });
  };

  return {
    data: {
      comparison,
      isLoading,
      error,
      isSynchronizing,
      matchingEntries,
      notMatchingList,
      matchingPaginated,
      notMatchingPaginated,
      firstMatchingNr,
      firstNotMatchingNr,
      employeeOptions,
      matchingPagination,
      notMatchingPagination,
      notMatchingSelections,
      hasUpdatesToSync,
      alreadyLinkedEmployeeIds,
    },
    actions: {
      handleClose,
      handleNotMatchingSelect,
      handleSynchronize,
      setMatchingPagination,
      setNotMatchingPagination,
    },
  };
};
